import {ReactElement, useContext, useEffect} from "react";
import styled from "styled-components";

import {PostEvenement} from "@/Api/evenements";
import {uploadFichierContrat} from "@/Api/fichier";
import IconCross from "@/assets/iconCross.svg?react";
import IconValidate from "@/assets/iconValide.svg?react";
import {AppContext} from "@/context/AppContext";
import {FournisseurContext} from "@/context/FournisseurContext";
import {HookFormContext, HookFormContextType} from "@/context/HookFormContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {calculePourcentage} from "@/fonction/calcule";
import {quicksandSemiBold} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {UseLoadingStateReturn} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";
import {FournisseurAndCampagne} from "@/ts/fournisseur";
import {KpiEnCours} from "@/ts/kpi";

import {Button} from "../Button/Button";
import {InputLabel} from "../Formulaire/Input/Input";
import {InputFileCustom} from "../Formulaire/Input/InputFileCustom";
import {TextareaLabel} from "../Formulaire/Input/Textarea";

const ContentModal = styled.form`
  padding: 1.25rem;
  min-width: 200px;
  container: Formulaire / inline-size;
  & h3 {
    margin: 0;
    ${quicksandSemiBold}
    font-size: 1.25rem;
    color: ${({theme}) => theme.colors.mediumPrimary};
    flex: 1;
    text-align: center;
  }
  @container modalContent (width <= 750px) {
    padding: 0.625rem 0;
  }
`;

const RecapKpiContent = styled.div`
  border-top: solid 3px ${({theme}) => theme.colors.darkPrimary};
  border-bottom: solid 3px ${({theme}) => theme.colors.darkPrimary};
  display: flex;
  justify-content: space-between;
  column-gap: 20px;
  row-gap: 15px;
  padding: 0.625rem 0;
  margin: 1rem auto;
  width: 80%;
  @container Formulaire (width <= 590px) {
    width: 90%;
    flex-direction: column;
  }
`;

const RecapKpiContentElement = styled.div`
  display: flex;
  gap: 10px;
  min-width: max-content;
  justify-content: center;
  flex: 1;

  & > p {
    color: ${({theme}) => theme.colors.darkPrimary};
    ${quicksandSemiBold}
  }
  & > span {
    color: ${({theme}) => theme.colors.lightPrimary};
    ${quicksandSemiBold}
  }
`;

const RecapKpiContentNewValue = styled.div`
  background-color: ${({theme}) => theme.colors.darkPrimary};
  display: flex;
  justify-content: space-between;
  column-gap: 20px;
  row-gap: 15px;
  padding: 0.625rem 0;
  margin: 1rem auto;
  width: 80%;
  @container Formulaire (width <= 590px) {
    width: 90%;
    flex-direction: column;
  }
`;

const RecapKpiContentNewValueElement = styled.div`
  display: flex;
  gap: 10px;
  min-width: max-content;
  justify-content: center;
  flex: 1;

  & > p {
    color: white;
    ${quicksandSemiBold}
  }
  & > span {
    color: white;
    ${quicksandSemiBold}
  }
`;

const ContentInputValeur = styled.div`
  max-width: 400px;
  margin: 0 auto;
`;
const ContentTextareaValeur = styled.div`
  margin: 0 auto;
  width: 80%;
  min-height: 140px;
  & textarea {
    min-height: 60px;
    max-width: calc(100% - 40px);
    min-width: calc(100% - 40px);
  }
  @container Formulaire (width <= 590px) {
    width: 90%;
    & div {
      max-width: none;
      width: 100%;
      padding: 0;
      & textarea {
        min-width: 0;
      }
    }
  }
`;

const ContentInputFile = styled.div`
  margin: 1rem auto;
  width: 80%;
`;

const Information = styled.p`
  color: ${({theme}) => theme.colors.mediumPrimary};
  font-size: 1rem;
  text-align: center;
`;

const FooterForm = styled.div`
  margin: 2rem auto 0 auto;
  display: flex;
  justify-content: space-around;
  width: 80%;
  gap: 1rem;
  & button {
    width: 220px;
    justify-content: center;
  }
  @container Formulaire (width <= 590px) {
    align-items: center;
    flex-direction: column;
  }
`;

/**
 * Ce composant est utilisé pour modifier la valeur d'un KPI.
 * Il est utilisé dans le composant TableauKpiEnCours.
 * @param contratId - L'id du contrat
 * @param kpi - Le KPI à modifier
 * @param closeModal - La fonction pour fermer le modal
 * @param loadingState - Le state de chargement
 * @param setContrat - La fonction pour mettre à jour le contrat (si pas de contexte FournisseurContext)
 */
export function ContentModalUpdateKpiValue({
  contratId,
  kpi,
  closeModal,
  loadingState,
  // si on ne peux pas utiliser le context FournisseurContext, on peut passer la fonction setContrat pour mettre à jour le contrat
  setContrat = undefined,
}: {
  contratId: number;
  kpi: KpiEnCours;
  closeModal: () => void;
  loadingState: UseLoadingStateReturn;
  setContrat?: (contrat: Contrat) => void;
}): ReactElement {
  // Ici on utilise pas le useGetContext car on veut rendre le composant même si on a pas de contexte FournisseurContext.
  // On utilise le steContrat à la place.
  const {setFournisseurAndCampagne, fournisseurAndCampagne} =
    useContext(FournisseurContext) || {};

  const {currentContremarque} = useGetContext<UserParamContextType>(
    UserParamContext,
    "UserParamContext"
  );
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  const {register, handleSubmit, setValue, control, watch, getValues} =
    useGetContext<HookFormContextType>(HookFormContext, "HookFormContext");

  type DataFormulaire = {incrementationValue: string; commentaire: string};

  // Récupère les valeurs du KPI dans le local storage pour les afficher dans les inputs
  useEffect(() => {
    if (localStorage.getItem(`kpi_${kpi.kpi_id}_incrementationValue`)) {
      setValue(
        "incrementationValue",
        localStorage.getItem(`kpi_${kpi.kpi_id}_incrementationValue`)
      );
    }
    if (localStorage.getItem(`kpi_${kpi.kpi_id}_commentaire`) !== null) {
      setValue(
        "commentaire",
        localStorage.getItem(`kpi_${kpi.kpi_id}_commentaire`)
      );
    }
  }, []);

  const handleNegate = (): void => {
    localStorage.removeItem(`kpi_${kpi.kpi_id}_incrementationValue`);
    localStorage.removeItem(`kpi_${kpi.kpi_id}_commentaire`);
    closeModal();
  };

  const onSubmit = (data: DataFormulaire): void => {
    localStorage.setItem(
      `kpi_${kpi.kpi_id}_incrementationValue`,
      data.incrementationValue
    );
    localStorage.setItem(`kpi_${kpi.kpi_id}_commentaire`, data.commentaire);
    // envoie la demande de modification de la valeur du KPI
    // Si elle passe on retire les valeurs du local storage
    loadingState.executeWithLoading(async () => {
      const allPieceJointe: File[] = getValues("pieceJointe");
      // envoie les fichiers et récupère les ids
      const allFichierIds = await Promise.all(
        allPieceJointe.map(async (pieceJointe: File) => {
          const formData = new FormData();
          formData.append("upload_fichier[fichier][file]", pieceJointe);
          formData.append("upload_fichier[classe]", "piece-jointe-evenement");
          const fichierSend = await uploadFichierContrat(
            API,
            contratId,
            formData,
            currentContremarque.value
          );
          return fichierSend.fichiers?.at(-1)?.fichier_id;
        })
      );

      const reponse = await PostEvenement(
        API,
        {
          contrat_id: contratId,
          kpi_id: Number(kpi.kpi_id),
          increment: Number(data.incrementationValue),
          commentaire: data.commentaire,
          fichiers: allFichierIds.filter(
            (fichierId): fichierId is number => fichierId !== undefined
          ),
        },
        currentContremarque.value,
        Number(kpi.kpi_id)
      );
      if (setContrat) {
        setContrat(reponse as Contrat);
      } else if (setFournisseurAndCampagne) {
        setFournisseurAndCampagne({
          ...(fournisseurAndCampagne as FournisseurAndCampagne),
          contrat: reponse,
        });
      }
    });
  };

  const incrementationValue = Number(watch("incrementationValue"));
  const newValeur = kpi.valeur + incrementationValue;
  return (
    <ContentModal onSubmit={handleSubmit(onSubmit)}>
      <h3>{kpi.nom}</h3>
      <RecapKpiContent>
        <RecapKpiContentElement>
          <p>Valeur actuelle : </p>
          <span>{kpi.valeur}</span>
        </RecapKpiContentElement>

        <RecapKpiContentElement>
          <p>Objectif : </p>
          <span>{kpi.objectif}</span>
        </RecapKpiContentElement>
        <RecapKpiContentElement>
          <p>Avancement : </p>
          <span>{`${calculePourcentage(kpi?.valeur, kpi?.objectif)} %`}</span>
        </RecapKpiContentElement>
      </RecapKpiContent>
      <ContentInputValeur>
        <InputLabel
          label="Ajouter à la valeur actuelle"
          type="number"
          step={0.01}
          min={0}
          placeholder="Ajouter à la valeur actuelle"
          isInColumn={false}
          required={true}
          {...register("incrementationValue")}
        />
      </ContentInputValeur>
      {incrementationValue !== 0 && (
        <RecapKpiContentNewValue>
          <RecapKpiContentNewValueElement>
            <p>Nouvelle valeur : </p>
            <span>{newValeur}</span>
          </RecapKpiContentNewValueElement>

          <RecapKpiContentNewValueElement>
            <p>Objectif : </p>
            <span>{kpi.objectif}</span>
          </RecapKpiContentNewValueElement>
          <RecapKpiContentNewValueElement>
            <p>Avancement : </p>
            <span>{`${calculePourcentage(newValeur, kpi?.objectif)} %`}</span>
          </RecapKpiContentNewValueElement>
        </RecapKpiContentNewValue>
      )}

      <ContentTextareaValeur>
        <TextareaLabel
          label="Commentaire"
          placeholder="Commentaire"
          {...register("commentaire")}
        />
        <ContentInputFile>
          <InputFileCustom
            accept=".pdf,application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            label="Piece jointe"
            control={control}
            name="pieceJointe"
            multiple={false}
          />
        </ContentInputFile>
      </ContentTextareaValeur>

      <Information>
        Votre changement sera soumis à validation auprès du fournisseur
      </Information>
      <FooterForm>
        <Button
          label="ANNULER"
          variant="negatif_secondary"
          icon={IconCross}
          type="button"
          onClick={handleNegate}
        />
        <Button
          label="VALIDER LE CHANGEMENT"
          type="submit"
          icon={IconValidate}
        />
      </FooterForm>
    </ContentModal>
  );
}
